import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardList,
  RotateCcw,
  CheckCircle2,
  Clock,
  AlertCircle,
  Calendar,
  Building2,
  AlertTriangle,
  FileText,
  Layers,
  Repeat,
  Plus,
} from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { useSession } from '../lib/session';
import { useMySubmissions, type SubmissionStatus } from '../lib/submissions';
import Shell from '../components/Shell';
import StockTransferDrawer from '../components/StockTransferDrawer';
import {
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
} from '../components/ui';

const FILTERS: Array<{ value: string; label: string; icon: LucideIcon }> = [
  { value: '', label: 'All Submissions', icon: ClipboardList },
  { value: 'pending', label: 'Waiting for Review', icon: Clock },
  { value: 'approved', label: 'Approved Entries', icon: CheckCircle2 },
  { value: 'rejected', label: 'Returned for Revision', icon: AlertCircle },
];

export default function MyEntries() {
  const actor = useSession((s) => s.actor);
  const [status, setStatus] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);
  const { data, isLoading, error, refetch } = useMySubmissions(actor?.id, status);

  const pendingCount = data?.filter((r) => r.status === 'pending').length ?? 0;

  return (
    <Shell
      title="My Site Submissions"
      crumb="Site Operations"
      actions={
        Boolean(actor) && (
          <button
            className="btn btn-primary btn-auto btn-sm"
            onClick={() => setTransferOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
          >
            <Repeat size={14} />
            <span>Add Stock Transfer</span>
          </button>
        )
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <p style={{ marginTop: 0, marginBottom: 18, color: 'var(--text-soft)', fontSize: 13.5 }}>
          Log of materials, attendance, and expenses you have submitted from construction sites.
          Entries are committed to project budgets once reviewed and approved by management.
        </p>

        {/* Toolbar */}
        <div
          className="toolbar"
          style={{
            background: 'var(--surface)',
            padding: '10px 14px',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            marginBottom: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {FILTERS.map((f) => {
            const IconComp = f.icon;
            const active = status === f.value;
            return (
              <button
                key={f.value}
                className={`btn btn-auto btn-sm ${active ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setStatus(f.value)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                }}
              >
                <IconComp size={14} />
                <span>{f.label}</span>
                {f.value === 'pending' && pendingCount > 0 && !status && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-xs)',
                      background: 'rgba(234, 88, 12, 0.2)',
                      color: '#ea580c',
                      fontWeight: 700,
                    }}
                  >
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}

          <div className="grow" />

          <button
            className="btn btn-auto btn-sm btn-ghost"
            onClick={() => void refetch()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              color: 'var(--text-soft)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <RotateCcw size={13} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Center-Aligned Submissions Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {isLoading ? (
              <TableSkeleton rows={5} cols={5} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : !data?.length ? (
              <EmptyState
                icon="clipboard"
                title={status ? 'No submissions match this filter' : 'No site entries recorded yet'}
                hint={
                  status
                    ? 'Try selecting a different filter above.'
                    : 'Open a project site and record material purchases, labour, or expenses.'
                }
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Entry Type</th>
                    <th>Summary & Details</th>
                    <th>Site Project</th>
                    <th>Review Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((r) => (
                    <tr key={`${r.table}-${r.id}`}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-soft)' }}>
                          <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
                          <span>{formatDate(r.date)}</span>
                        </span>
                      </td>

                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                          {r.kind}
                        </span>
                      </td>

                      <td style={{ whiteSpace: 'normal', maxWidth: 360 }}>
                        <div style={{ fontSize: 13, color: 'var(--text)' }}>
                          {r.summary}
                        </div>

                        {/* Rejection reason callout */}
                        {r.status === 'rejected' && r.reviewNote && (
                          <div
                            style={{
                              marginTop: 6,
                              padding: '6px 10px',
                              borderRadius: 'var(--radius-xs)',
                              fontSize: 12,
                              background: 'rgba(239, 68, 68, 0.08)',
                              color: '#dc2626',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              display: 'inline-flex',
                              alignItems: 'flex-start',
                              gap: 6,
                              textAlign: 'left',
                            }}
                          >
                            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div>
                              <strong>Reason for return:</strong> {r.reviewNote}
                            </div>
                          </div>
                        )}
                      </td>

                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500, color: 'var(--text-soft)' }}>
                          <Building2 size={12} style={{ color: 'var(--text-faint)' }} />
                          <span>{r.projectName}</span>
                        </span>
                      </td>

                      <td>
                        {r.status === 'approved' && (
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              padding: '3px 9px',
                              borderRadius: 'var(--radius-xs)',
                              background: 'rgba(22, 163, 74, 0.1)',
                              color: '#16a34a',
                              border: '1px solid rgba(22, 163, 74, 0.2)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <CheckCircle2 size={12} />
                            <span>Approved</span>
                          </span>
                        )}
                        {r.status === 'rejected' && (
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              padding: '3px 9px',
                              borderRadius: 'var(--radius-xs)',
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#dc2626',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <AlertCircle size={12} />
                            <span>Returned</span>
                          </span>
                        )}
                        {r.status === 'pending' && (
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              padding: '3px 9px',
                              borderRadius: 'var(--radius-xs)',
                              background: 'rgba(234, 88, 12, 0.1)',
                              color: '#ea580c',
                              border: '1px solid rgba(234, 88, 12, 0.2)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <Clock size={12} />
                            <span>Waiting Review</span>
                          </span>
                        )}

                        {r.reviewedAt && (
                          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 3 }}>
                            {formatDate(r.reviewedAt.slice(0, 10))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {transferOpen && <StockTransferDrawer onClose={() => setTransferOpen(false)} />}
    </Shell>
  );
}
